// Personal UI language (LOC-305): the client of /api/account/preferences and
// /api/l10n/bootstrap (contracts v1), and the controller that applies the
// signed-in account's stored choice and the server's language resolution to
// the localization runtime.
//
// The server profile is the only source of truth. The controller keeps the
// last server-confirmed value of the current account in memory only, stamped
// with that account; it never writes the profile on its own (not on sign-in,
// not on load, not from the browser language), and it reports "saved" only
// for a value the server confirmed.
//
// Account binding: the session cookie is shared by every tab, so a request
// sent on it may already belong to another account. Every account-bound read
// and write therefore first mints the existing session access token
// (POST /api/auth/session/token), checks that the server minted it for the
// account this UI shows, and sends only that Bearer without the cookie. The
// server answers a Bearer request for the token's own account or refuses it
// (it dies with its session and never falls back to the cookie). The token
// lives only for that one operation; nothing is cached or stored.

import { DEFAULT_BASE_PATH } from "./loader";
import type { ResolutionSource } from "./language";
import type { LocalizationRuntime } from "./runtime";
import type { ClientIdentity, FetchLike } from "./types";

export const PREFERENCES_PATH = "/api/account/preferences";
export const BOOTSTRAP_PATH = "/api/l10n/bootstrap";
export const SESSION_TOKEN_PATH = "/api/auth/session/token";

const LANGUAGE_TAG = /^[a-z]{2,3}(-[A-Z][a-z]{3})?(-([A-Z]{2}|[0-9]{3}))?$/;
const REVISION = /^[0-9a-f]{16}$/;
const MANIFEST_URL = /^\/l10n\/[a-z][a-z0-9-]+\/v[1-9][0-9]*\/manifest\.json$/;
const SOURCES: readonly ResolutionSource[] = ["explicit_profile", "browser", "platform_default", "fallback_unavailable"];
const BOOTSTRAP_KEYS = ["schema_version", "settings_revision", "ui_language", "available_languages", "catalog", "sources", "client_hints_used"];
/** CSRF marker the server requires on the cookie-authenticated session endpoints. */
const SESSION_HEADER = { "X-Voltie-Session-Token": "1" } as const;

/** GET/PATCH response (preferences.schema.json). ui_language null = automatic. */
export interface PreferencesDocument {
  readonly schema_version: 1;
  readonly revision: number;
  readonly ui_language: string | null;
}

/** JSON Merge Patch (preferences-patch.schema.json): only the field being changed. */
export interface PreferencesPatch {
  readonly ui_language: string | null;
}

/** Bootstrap response (bootstrap.schema.json). */
export interface LanguageBootstrap {
  readonly schema_version: 1;
  readonly settings_revision: string;
  readonly ui_language: string;
  readonly available_languages: readonly string[];
  readonly catalog: { readonly revision: string; readonly manifest_url: string };
  readonly sources: { readonly ui_language: ResolutionSource };
  readonly client_hints_used?: readonly "accept_language"[];
}

/**
 * A session access token and the account the server minted it for
 * (`user_id` of the mint answer, never a decoded token). Used for one
 * operation and then dropped.
 */
export interface AccountAccess {
  readonly accountId: string;
  readonly token: string;
}

export type PreferencesErrorCode =
  | "unauthenticated"
  | "account_changed"
  | "forbidden"
  | "unsupported_language"
  | "invalid_request"
  | "unavailable"
  | "network"
  | "timeout"
  | "aborted"
  | "invalid_response";

export class PreferencesError extends Error {
  readonly code: PreferencesErrorCode;
  readonly status: number | null;

  constructor(code: PreferencesErrorCode, status: number | null, detail: string) {
    super(`${code}: ${detail}`);
    this.name = "PreferencesError";
    this.code = code;
    this.status = status;
  }
}

export interface PreferencesApi {
  /** Mints a session access token from the browser session (cookie and CSRF marker). */
  access(signal?: AbortSignal): Promise<AccountAccess>;
  get(access: AccountAccess, signal?: AbortSignal): Promise<PreferencesDocument>;
  patch(patch: PreferencesPatch, access: AccountAccess, signal?: AbortSignal): Promise<PreferencesDocument>;
  /** With `access` null the request carries no credentials at all: a guest resolution. */
  bootstrap(client: string, contract: number, access: AccountAccess | null, signal?: AbortSignal): Promise<LanguageBootstrap>;
}

export interface PreferencesApiOptions {
  readonly fetch?: FetchLike;
  /** Prefix of the API paths; default "" (same origin). */
  readonly baseUrl?: string;
  readonly timeoutMs?: number;
}

const isTag = (value: unknown): value is string => typeof value === "string" && LANGUAGE_TAG.test(value);
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

function parsePreferences(body: unknown): PreferencesDocument {
  const doc = body as Partial<PreferencesDocument> | null;
  if (
    !isRecord(doc) ||
    doc.schema_version !== 1 ||
    !Number.isSafeInteger(doc.revision) ||
    (doc.revision as number) < 0 ||
    !(doc.ui_language === null || isTag(doc.ui_language)) ||
    Object.keys(doc).length !== 3
  ) {
    throw new PreferencesError("invalid_response", null, "not a v1 preferences document");
  }
  return { schema_version: 1, revision: doc.revision as number, ui_language: doc.ui_language as string | null };
}

/** A v1 bootstrap document whose manifest URL matches `manifestUrl` (the public pointer unless a private route says otherwise). */
export function parseBootstrap(body: unknown, manifestUrl: RegExp = MANIFEST_URL): LanguageBootstrap {
  const invalid = () => new PreferencesError("invalid_response", null, "not a v1 bootstrap");
  if (!isRecord(body) || !Object.keys(body).every((k) => BOOTSTRAP_KEYS.includes(k))) throw invalid();
  const { settings_revision: revision, ui_language: language, available_languages: languages, catalog, sources, client_hints_used: hints } = body;
  if (
    body.schema_version !== 1 ||
    typeof revision !== "string" ||
    revision.length < 1 ||
    revision.length > 64 ||
    !isTag(language) ||
    !Array.isArray(languages) ||
    !languages.every(isTag) ||
    new Set(languages).size !== languages.length ||
    !languages.includes("en") ||
    !languages.includes(language) ||
    !isRecord(catalog) ||
    Object.keys(catalog).length !== 2 ||
    typeof catalog.revision !== "string" ||
    !REVISION.test(catalog.revision) ||
    typeof catalog.manifest_url !== "string" ||
    !manifestUrl.test(catalog.manifest_url) ||
    !isRecord(sources) ||
    Object.keys(sources).length !== 1 ||
    !SOURCES.includes(sources.ui_language as ResolutionSource) ||
    (hints !== undefined && !(Array.isArray(hints) && hints.length <= 1 && hints.every((h) => h === "accept_language")))
  ) {
    throw invalid();
  }
  return body as unknown as LanguageBootstrap;
}

function parseAccess(body: unknown): AccountAccess {
  const doc = body as { access_token?: unknown; user_id?: unknown } | null;
  if (!isRecord(doc) || typeof doc.access_token !== "string" || !doc.access_token || !Number.isSafeInteger(doc.user_id) || (doc.user_id as number) < 1) {
    throw new PreferencesError("invalid_response", null, "not a session access token");
  }
  return { accountId: String(doc.user_id), token: doc.access_token };
}

function errorFor(status: number, code: unknown): PreferencesErrorCode {
  if (status === 401) return "unauthenticated";
  if (status === 403) return "forbidden";
  if (status === 400 && code === "unsupported_language") return "unsupported_language";
  if (status >= 500) return "unavailable";
  return "invalid_request";
}

/** How a request authenticates: the browser session (mint only), one access token, or not at all. */
export type Credential = "session" | AccountAccess | null;

export type ApiRequest = (path: string, init: RequestInit, credential: Credential, signal?: AbortSignal) => Promise<unknown>;

/** The JSON request of the account API clients: uncached, timed, the cookie only for the mint, errors as PreferencesError. */
export function createApiRequest(options: PreferencesApiOptions = {}): ApiRequest {
  const fetchImpl: FetchLike = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
  const baseUrl = options.baseUrl ?? "";
  const timeoutMs = options.timeoutMs ?? 15_000;

  return async function request(path, init, credential, signal) {
    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);
    const onAbort = () => controller.abort();
    if (signal?.aborted) controller.abort();
    else signal?.addEventListener("abort", onAbort, { once: true });
    const auth: Record<string, string> =
      credential === "session" ? { ...SESSION_HEADER } : credential ? { Authorization: `Bearer ${credential.token}` } : {};
    try {
      let response: Response;
      try {
        response = await fetchImpl(`${baseUrl}${path}`, {
          ...init,
          // Only the mint uses the ambient cookie; a token request must not carry it.
          credentials: credential === "session" ? "same-origin" : "omit",
          cache: "no-store",
          headers: { Accept: "application/json", ...(init.headers as Record<string, string> | undefined), ...auth },
          signal: controller.signal,
        });
      } catch (e) {
        if (timedOut) throw new PreferencesError("timeout", null, path);
        if (controller.signal.aborted) throw new PreferencesError("aborted", null, path);
        throw new PreferencesError("network", null, e instanceof Error ? e.message : path);
      }
      const json = (response.headers.get("content-type") ?? "").includes("application/json");
      const body: unknown = json ? await response.json().catch(() => undefined) : undefined;
      if (!response.ok) {
        const code = (body as { error?: unknown } | undefined)?.error;
        throw new PreferencesError(errorFor(response.status, code), response.status, `HTTP ${response.status}`);
      }
      if (body === undefined) throw new PreferencesError("invalid_response", response.status, "not JSON");
      return body;
    } catch (e) {
      if (e instanceof PreferencesError) throw e;
      if (timedOut) throw new PreferencesError("timeout", null, path);
      if (controller.signal.aborted) throw new PreferencesError("aborted", null, path);
      throw new PreferencesError("network", null, e instanceof Error ? e.message : path);
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
    }
  };
}

export function createPreferencesApi(options: PreferencesApiOptions = {}): PreferencesApi {
  const request = createApiRequest(options);

  return {
    async access(signal) {
      return parseAccess(await request(SESSION_TOKEN_PATH, { method: "POST" }, "session", signal));
    },
    async get(access, signal) {
      return parsePreferences(await request(PREFERENCES_PATH, { method: "GET" }, access, signal));
    },
    async patch(patch, access, signal) {
      const body = JSON.stringify({ ui_language: patch.ui_language });
      const headers = { "Content-Type": "application/merge-patch+json" };
      return parsePreferences(await request(PREFERENCES_PATH, { method: "PATCH", headers, body }, access, signal));
    },
    async bootstrap(client, contract, access, signal) {
      // `contract` selects the pointer of the client contract this build embeds; it grants nothing.
      const query = `?client=${encodeURIComponent(client)}&contract=${encodeURIComponent(String(contract))}`;
      return parseBootstrap(await request(`${BOOTSTRAP_PATH}${query}`, { method: "GET" }, access, signal));
    },
  };
}

// ---------------------------------------------------------------- controller

export type AccountId = number | string;

/**
 * unknown: the auth projection has not answered yet; signed_out: no account,
 * the browser rule applies; loading/ready/error: the account's stored profile;
 * account_changed: the browser session no longer belongs to the account shown
 * (another tab signed in or out), so nothing was read or written for it and
 * the app refetches its identity projection.
 */
export type ProfileStatus = "unknown" | "signed_out" | "loading" | "ready" | "error" | "account_changed";

export interface StoredLanguage {
  readonly revision: number;
  /** null = automatic (browser, then English). */
  readonly uiLanguage: string | null;
}

export interface PreferencesState {
  /** Account of this state, from the server's identity projection; null signed out; undefined unknown. */
  readonly accountId: string | null | undefined;
  readonly status: ProfileStatus;
  /** Last value the server confirmed for this account; null until it answered. */
  readonly stored: StoredLanguage | null;
  /** Why the last read failed (the stored value, if any, is still the last confirmed one). */
  readonly loadError: PreferencesErrorCode | null;
  readonly save: {
    /** saved only after the server confirmed the latest request; error when it was not saved. */
    readonly status: "idle" | "saving" | "saved" | "error";
    /** The value of the latest save request (null = back to automatic); undefined before any. */
    readonly requested: string | null | undefined;
    readonly error: PreferencesErrorCode | null;
  };
}

export type SaveResult =
  | { readonly ok: true; readonly stored: StoredLanguage }
  | { readonly ok: false; readonly reason: PreferencesErrorCode | "signed_out" | "superseded" };

export interface PreferencesController {
  getSnapshot(): PreferencesState;
  subscribe(listener: () => void): () => void;
  /**
   * The signed-in account as the server's identity projection reports it
   * (`/api/auth/me`), or null when signed out. Never a decoded or cached
   * token. A different account clears the previous one's profile at once,
   * returns the runtime to the browser rule and ignores every pending answer
   * and queued save of the previous account.
   */
  setAccount(accountId: AccountId | null): void;
  /** Reads the profile and the bootstrap again (another device may have changed them). */
  reload(): Promise<void>;
  /** Saves an explicit published language, or null for automatic. Only `ui_language` is sent. */
  save(uiLanguage: string | null): Promise<SaveResult>;
}

export interface PreferencesControllerOptions {
  readonly runtime: Pick<LocalizationRuntime, "setIntent" | "identity">;
  readonly api?: PreferencesApi;
}

const toStored = (doc: PreferencesDocument): StoredLanguage => ({ revision: doc.revision, uiLanguage: doc.ui_language });
const codeOf = (e: unknown): PreferencesErrorCode => (e instanceof PreferencesError ? e.code : "network");

/**
 * The bootstrap's language if it answers for exactly this build (client,
 * contract pointer and base language) and agrees with the stored profile it
 * is applied with; null otherwise. It never adds a language: the runtime uses
 * it only when its own verified manifest publishes it.
 */
export function bootstrapLanguage(boot: LanguageBootstrap, identity: ClientIdentity, stored: StoredLanguage | null): string | null {
  if (boot.catalog.manifest_url !== `${DEFAULT_BASE_PATH}/${identity.clientId}/v${identity.contractVersion}/manifest.json`) return null;
  if (!boot.available_languages.includes(identity.baseLanguage)) return null;
  const source = boot.sources.ui_language;
  const explicit = stored?.uiLanguage ?? null;
  const fromProfile = source === "explicit_profile" || source === "fallback_unavailable";
  if (fromProfile !== (explicit !== null)) return null;
  if (source === "explicit_profile" && boot.ui_language !== explicit) return null;
  return boot.ui_language;
}

interface SaveRequest {
  readonly value: string | null;
  readonly epoch: number;
  readonly account: string;
  readonly resolve: (result: SaveResult) => void;
}

interface Intent {
  readonly explicit: string | null;
  readonly projected: string | null;
}

export function createPreferencesController(options: PreferencesControllerOptions): PreferencesController {
  const { runtime } = options;
  const { identity } = runtime;
  const api = options.api ?? createPreferencesApi();
  const idle = { status: "idle", requested: undefined, error: null } as const;

  let state: PreferencesState = { accountId: undefined, status: "unknown", stored: null, loadError: null, save: idle };
  const listeners = new Set<() => void>();
  const publish = (next: Partial<PreferencesState>) => {
    state = { ...state, ...next };
    for (const listener of [...listeners]) listener();
  };

  // Bumped on every account change: an answer of an older epoch is never applied.
  let epoch = 0;
  let loadSeq = 0;
  let loadAbort: AbortController | null = null;
  let queued: SaveRequest | null = null;
  let sending = false;
  // Bumped by every save(): a bootstrap read before the latest choice never decides after it.
  let choices = 0;
  // The server's resolution for the current account (or guest); null = client rule.
  let projection: string | null = null;
  // What the runtime was last told; the runtime starts in automatic mode without a projection.
  let applied: Intent = { explicit: null, projected: null };

  function apply() {
    const next: Intent = { explicit: state.stored?.uiLanguage ?? null, projected: projection };
    if (next.explicit === applied.explicit && next.projected === applied.projected) return;
    applied = next;
    void runtime.setIntent(next);
  }

  const savePending = () => sending || queued !== null;

  /** Takes a server answer for the current account; an older revision never replaces a newer one. */
  function accept(doc: PreferencesDocument): boolean {
    if (state.stored && doc.revision < state.stored.revision) return false;
    publish({ stored: toStored(doc) });
    return true;
  }

  /** The session is no longer the account shown: keep its confirmed value, read and write nothing. */
  function accountChanged(code: PreferencesErrorCode) {
    publish({ status: "account_changed", loadError: code });
  }

  const bootstrapFor = (access: AccountAccess | null, signal?: AbortSignal) =>
    api.bootstrap(identity.clientId, identity.contractVersion, access, signal);

  /**
   * Takes a bootstrap answer (or its failure: the client rule) only if no
   * choice was made since it was requested; then applies the stored value.
   */
  function project(boot: PromiseSettledResult<LanguageBootstrap>, choicesAtStart: number) {
    if (choicesAtStart !== choices || savePending()) return;
    projection = boot.status === "fulfilled" ? bootstrapLanguage(boot.value, identity, state.stored) : null;
    apply();
  }

  function loadGuest(): void {
    const forEpoch = epoch;
    const choicesAtStart = choices;
    const abort = new AbortController();
    loadAbort = abort;
    void bootstrapFor(null, abort.signal).then(
      (boot) => {
        if (forEpoch === epoch) project({ status: "fulfilled", value: boot }, choicesAtStart);
      },
      () => {
        // Offline or unavailable: the client rule already applies.
      },
    );
  }

  function load(): Promise<void> {
    const id = state.accountId;
    if (id == null) return Promise.resolve();
    const forEpoch = epoch;
    const seq = ++loadSeq;
    const choicesAtStart = choices;
    loadAbort?.abort();
    const abort = new AbortController();
    loadAbort = abort;
    if (!state.stored && (state.status !== "loading" || state.loadError)) publish({ status: "loading", loadError: null });
    const stale = () => forEpoch !== epoch || seq !== loadSeq;
    const failed = (code: PreferencesErrorCode) => {
      if (code === "unauthenticated") accountChanged(code);
      else publish({ status: state.stored ? "ready" : "error", loadError: code });
    };
    return (async () => {
      let access: AccountAccess;
      try {
        access = await api.access(abort.signal);
      } catch (e) {
        if (!stale()) failed(codeOf(e));
        return;
      }
      if (stale()) return;
      if (access.accountId !== id) {
        accountChanged("account_changed");
        return;
      }
      const [doc, boot] = await Promise.allSettled([api.get(access, abort.signal), bootstrapFor(access, abort.signal)]);
      if (stale()) return;
      if (doc.status === "rejected") {
        failed(codeOf(doc.reason));
        return;
      }
      accept(doc.value);
      publish({ status: "ready", loadError: null });
      // While a save is pending its confirmation decides; the user's latest request is not undone.
      if (savePending()) return;
      project(boot, choicesAtStart);
      apply();
    })();
  }

  async function send(request: SaveRequest): Promise<SaveResult> {
    const current = () => request.epoch === epoch;
    try {
      const access = await api.access();
      if (!current()) return { ok: false, reason: "account_changed" };
      if (access.accountId !== request.account) {
        accountChanged("account_changed");
        publish({ save: { status: "error", requested: request.value, error: "account_changed" } });
        return { ok: false, reason: "account_changed" };
      }
      // A newer choice arrived while minting: this one is never sent.
      if (queued) return { ok: false, reason: "superseded" };
      const doc = await api.patch({ ui_language: request.value }, access);
      if (!current()) return { ok: false, reason: "account_changed" };
      accept(doc);
      // A newer choice follows; it alone decides what is applied and shown.
      if (queued) return { ok: false, reason: "superseded" };
      projection = null;
      publish({ status: "ready", loadError: null, save: { status: "saved", requested: request.value, error: null } });
      apply();
      // The server's resolution of the new choice; nothing newer may have decided by then.
      const choicesNow = choices;
      void bootstrapFor(access).then(
        (boot) => {
          if (current()) project({ status: "fulfilled", value: boot }, choicesNow);
        },
        () => {},
      );
      return { ok: true, stored: toStored(doc) };
    } catch (e) {
      const code = codeOf(e);
      if (!current()) return { ok: false, reason: "account_changed" };
      if (queued) return { ok: false, reason: "superseded" };
      if (code === "unauthenticated") accountChanged(code);
      publish({ save: { status: "error", requested: request.value, error: code } });
      return { ok: false, reason: code };
    }
  }

  function pump(): void {
    if (sending || !queued) return;
    const request = queued;
    queued = null;
    if (request.epoch !== epoch) {
      request.resolve({ ok: false, reason: "account_changed" });
      return pump();
    }
    sending = true;
    void send(request).then((result) => {
      sending = false;
      request.resolve(result);
      pump();
    });
  }

  return {
    getSnapshot: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    setAccount(accountId) {
      const key = accountId == null ? null : String(accountId);
      if (key === state.accountId) return;
      epoch += 1;
      loadAbort?.abort();
      loadAbort = null;
      if (queued) {
        queued.resolve({ ok: false, reason: "account_changed" });
        queued = null;
      }
      projection = null;
      publish({ accountId: key, status: key === null ? "signed_out" : "loading", stored: null, loadError: null, save: idle });
      apply();
      if (key !== null) void load();
      else loadGuest();
    },
    reload() {
      if (state.accountId === null) loadGuest();
      return load();
    },
    save(uiLanguage) {
      const account = state.accountId;
      if (account == null) return Promise.resolve({ ok: false, reason: "signed_out" });
      if (uiLanguage !== null && !isTag(uiLanguage)) return Promise.resolve({ ok: false, reason: "invalid_request" });
      choices += 1;
      return new Promise<SaveResult>((resolve) => {
        queued?.resolve({ ok: false, reason: "superseded" });
        queued = { value: uiLanguage, epoch, account, resolve };
        publish({ save: { status: "saving", requested: uiLanguage, error: null } });
        pump();
      });
    },
  };
}
